import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Card, Row, Col, Button, Form, Alert } from "react-bootstrap";
import CommonAppointmentCalendar from "./CommonAppointmentCalendar";
import { getDoctorsAction } from "../../../store/actions/DoctorsAction";
import PageTitle from "../../layouts/PageTitle";

const AppointmentCalendarTest = () => {
  const dispatch = useDispatch();
  const doctors = useSelector((state) => state.doctors.doctors || []);

  // Test data state
  const [testData, setTestData] = useState({
    leadId: "1",
    patientId: "1",
    doctorId: doctors[0]?.doctor_id || "1",
    adminId: "1",
    currentLeadId: "1",
    currentPatientId: "1",
  });

  const [selectedExample, setSelectedExample] = useState("telecaller");
  const [notifications, setNotifications] = useState([]);

  // Load doctors on component mount
  useEffect(() => {
    dispatch(getDoctorsAction());
  }, [dispatch]);

  // Update doctorId when doctors are loaded
  useEffect(() => {
    if (doctors.length > 0 && !testData.doctorId) {
      setTestData((prev) => ({
        ...prev,
        doctorId: doctors[0].doctor_id,
      }));
    }
  }, [doctors, testData.doctorId]);

  const addNotification = (type, message) => {
    const id = Date.now();
    setNotifications((prev) => [...prev, { id, type, message }]);
    setTimeout(() => {
      setNotifications((prev) => prev.filter((n) => n.id !== id));
    }, 5000);
  };

  // Example configurations
  const examples = {
    telecaller: {
      title: "Telecaller Calendar",
      description: "Basic booking and cancellation for telecaller module",
      props: {
        leadId: testData.leadId,
        doctorId: testData.doctorId,
        currentLeadId: testData.currentLeadId,
        appointmentType: "telecaller",
        showReschedule: false,
        showPayment: false,
        showCancel: true,
        showBook: true,
        className: "telecaller-calendar",
        onBookingSuccess: (_data) =>
          addNotification(
            "success",
            "Telecaller: Appointment booked successfully!"
          ),
        onCancelSuccess: (_id) =>
          addNotification(
            "info",
            "Telecaller: Appointment cancelled successfully!"
          ),
      },
    },
    servicecaller: {
      title: "Service Caller Calendar",
      description:
        "Booking, cancellation, and payment for service caller module",
      props: {
        leadId: testData.leadId,
        patientId: testData.patientId,
        doctorId: testData.doctorId,
        currentLeadId: testData.currentLeadId,
        currentPatientId: testData.currentPatientId,
        appointmentType: "service_caller",
        showReschedule: false,
        showPayment: true,
        showCancel: true,
        showBook: true,
        className: "service-caller-calendar",
        onBookingSuccess: (_data) =>
          addNotification(
            "success",
            "Service Caller: Appointment booked successfully!"
          ),
        onCancelSuccess: (_id) =>
          addNotification(
            "info",
            "Service Caller: Appointment cancelled successfully!"
          ),
        onPaymentSuccess: (_data) =>
          addNotification(
            "success",
            "Service Caller: Payment collected successfully!"
          ),
      },
    },
    receptionist: {
      title: "Receptionist Calendar",
      description:
        "Full features: booking, cancellation, payment, and reschedule",
      props: {
        patientId: testData.patientId,
        doctorId: testData.doctorId,
        currentPatientId: testData.currentPatientId,
        appointmentType: "receptionist",
        adminId: testData.adminId,
        showReschedule: true,
        showPayment: true,
        showCancel: true,
        showBook: true,
        className: "receptionist-calendar",
        onBookingSuccess: (_data) =>
          addNotification(
            "success",
            "Receptionist: Appointment booked successfully!"
          ),
        onCancelSuccess: (_id) =>
          addNotification(
            "info",
            "Receptionist: Appointment cancelled successfully!"
          ),
        onPaymentSuccess: (_data) =>
          addNotification(
            "success",
            "Receptionist: Payment collected successfully!"
          ),
        onRescheduleSuccess: (_data) =>
          addNotification(
            "success",
            "Receptionist: Appointment rescheduled successfully!"
          ),
      },
    },
    viewonly: {
      title: "View Only Calendar",
      description: "Read-only calendar with no interaction",
      props: {
        leadId: testData.leadId,
        doctorId: testData.doctorId,
        currentLeadId: testData.currentLeadId,
        appointmentType: "view_only",
        showReschedule: false,
        showPayment: false,
        showCancel: false,
        showBook: false,
        className: "view-only-calendar",
      },
    },
  };

  const handleDataChange = (field, value) => {
    setTestData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const currentExample = examples[selectedExample];

  return (
    <div className="container-fluid">
      <PageTitle
        motherMenu="Components"
        activeMenu="Appointment Calendar Test"
      />

      {/* Notifications */}
      <div
        className="position-fixed"
        style={{ top: "20px", right: "20px", zIndex: 9999 }}
      >
        {notifications.map((notification) => (
          <Alert
            key={notification.id}
            variant={notification.type}
            className="mb-2"
          >
            {notification.message}
          </Alert>
        ))}
      </div>

      <Row>
        <Col xl={12}>
          <Card>
            <Card.Header>
              <h4 className="card-title">
                Common Appointment Calendar - Test Examples
              </h4>
            </Card.Header>
            <Card.Body>
              <div className="mb-4">
                <h6>Instructions:</h6>
                <ul>
                  <li>
                    Select an example from the dropdown to see different
                    configurations
                  </li>
                  <li>Update test data in the form below</li>
                  <li>Select a doctor from the calendar dropdown</li>
                  <li>Click on available slots (white) to book appointments</li>
                  <li>
                    Click on your appointments (yellow/green) to see options
                  </li>
                  <li>Navigate weeks using the Previous/Next/Today buttons</li>
                </ul>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row>
        <Col xl={4}>
          <Card>
            <Card.Header>
              <h5>Test Configuration</h5>
            </Card.Header>
            <Card.Body>
              <Form>
                <Form.Group className="mb-3">
                  <Form.Label>Example Type</Form.Label>
                  <Form.Select
                    value={selectedExample}
                    onChange={(e) => setSelectedExample(e.target.value)}
                  >
                    <option value="telecaller">Telecaller</option>
                    <option value="servicecaller">Service Caller</option>
                    <option value="receptionist">Receptionist</option>
                    <option value="viewonly">View Only</option>
                  </Form.Select>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Lead ID</Form.Label>
                  <Form.Control
                    type="text"
                    value={testData.leadId}
                    onChange={(e) => handleDataChange("leadId", e.target.value)}
                    placeholder="Enter lead ID"
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Patient ID</Form.Label>
                  <Form.Control
                    type="text"
                    value={testData.patientId}
                    onChange={(e) =>
                      handleDataChange("patientId", e.target.value)
                    }
                    placeholder="Enter patient ID"
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Doctor ID</Form.Label>
                  <Form.Control
                    type="text"
                    value={testData.doctorId}
                    onChange={(e) =>
                      handleDataChange("doctorId", e.target.value)
                    }
                    placeholder="Enter doctor ID"
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Admin ID</Form.Label>
                  <Form.Control
                    type="text"
                    value={testData.adminId}
                    onChange={(e) =>
                      handleDataChange("adminId", e.target.value)
                    }
                    placeholder="Enter admin ID"
                  />
                </Form.Group>

                <div className="mb-3">
                  <h6>Available Doctors:</h6>
                  {doctors.length > 0 ? (
                    <ul className="list-unstyled">
                      {doctors.slice(0, 5).map((doctor) => (
                        <li key={doctor.doctor_id} className="mb-1">
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() =>
                              handleDataChange("doctorId", doctor.doctor_id)
                            }
                          >
                            {doctor.doctor_name} (ID: {doctor.doctor_id})
                          </Button>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-muted">Loading doctors...</p>
                  )}
                </div>

                <div className="mb-3">
                  <h6>Current Configuration:</h6>
                  <div className="bg-light p-3 rounded">
                    <p>
                      <strong>Type:</strong> {currentExample.title}
                    </p>
                    <p>
                      <strong>Description:</strong> {currentExample.description}
                    </p>
                    <p>
                      <strong>Features:</strong>
                    </p>
                    <ul>
                      <li>Book: {currentExample.props.showBook ? "✓" : "✗"}</li>
                      <li>
                        Cancel: {currentExample.props.showCancel ? "✓" : "✗"}
                      </li>
                      <li>
                        Payment: {currentExample.props.showPayment ? "✓" : "✗"}
                      </li>
                      <li>
                        Reschedule:{" "}
                        {currentExample.props.showReschedule ? "✓" : "✗"}
                      </li>
                    </ul>
                  </div>
                </div>
              </Form>
            </Card.Body>
          </Card>
        </Col>

        <Col xl={8}>
          <Card>
            <Card.Header>
              <h5>{currentExample.title}</h5>
              <p className="text-muted">{currentExample.description}</p>
            </Card.Header>
            <Card.Body>
              <CommonAppointmentCalendar
                {...currentExample.props}
                // Override with current test data
                leadId={testData.leadId}
                patientId={testData.patientId}
                doctorId={testData.doctorId}
                adminId={testData.adminId}
                currentLeadId={testData.currentLeadId}
                currentPatientId={testData.currentPatientId}
              />
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row>
        <Col xl={12}>
          <Card>
            <Card.Header>
              <h5>Color Legend & Information</h5>
            </Card.Header>
            <Card.Body>
              <Row>
                <Col md={6}>
                  <h6>Color Coding:</h6>
                  <div className="d-flex flex-wrap gap-3 mb-3">
                    <div className="d-flex align-items-center">
                      <div
                        className="me-2"
                        style={{
                          width: "20px",
                          height: "20px",
                          backgroundColor: "#ffffff",
                          border: "1px solid #dee2e6",
                          borderRadius: "4px",
                        }}
                      ></div>
                      <span>Available</span>
                    </div>
                    <div className="d-flex align-items-center">
                      <div
                        className="me-2"
                        style={{
                          width: "20px",
                          height: "20px",
                          backgroundColor: "#ffc107",
                          border: "1px solid #ffcd39",
                          borderRadius: "4px",
                        }}
                      ></div>
                      <span>Your Appointment</span>
                    </div>
                    <div className="d-flex align-items-center">
                      <div
                        className="me-2"
                        style={{
                          width: "20px",
                          height: "20px",
                          backgroundColor: "#28a745",
                          border: "1px solid #34ce57",
                          borderRadius: "4px",
                        }}
                      ></div>
                      <span>Confirmed</span>
                    </div>
                    <div className="d-flex align-items-center">
                      <div
                        className="me-2"
                        style={{
                          width: "20px",
                          height: "20px",
                          backgroundColor: "#800020",
                          border: "1px solid #a0002a",
                          borderRadius: "4px",
                        }}
                      ></div>
                      <span>Booked by Others</span>
                    </div>
                  </div>
                </Col>
                <Col md={6}>
                  <h6>Usage Tips:</h6>
                  <ul>
                    <li>Click on white slots to book new appointments</li>
                    <li>
                      Click on yellow/green slots to see options (cancel,
                      payment, reschedule)
                    </li>
                    <li>Use Previous/Next buttons to navigate weeks</li>
                    <li>Click "Today" to go back to current week</li>
                    <li>Select different doctors to see their availability</li>
                  </ul>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default AppointmentCalendarTest;
